import React, { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider } from '@/contexts/AuthContext';
import { BookProvider } from '@/contexts/BookContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { VoiceChatProvider } from '@/contexts/VoiceChatContext';
import { Toaster } from "@/components/ui/toaster";
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';
import SessionManager from './components/admin/SessionManager';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Index'));
const LandingV2 = lazy(() => import('./pages/LandingV2'));
const Library = lazy(() => import('./pages/Library'));
const Chat = lazy(() => import('./pages/Chat'));
const BookDetails = lazy(() => import('./pages/BookDetails'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Waitlist = lazy(() => import('./pages/Waitlist'));
const AdminInviteCodes = lazy(() => import('./pages/AdminInviteCodes'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminDocuments = lazy(() => import('./pages/AdminDocuments'));
const Settings = lazy(() => import('./pages/Settings'));

// Loading component for suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Home Route component that redirects authenticated users to the library
const HomeRoute = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/library');
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return <PageLoader />;
  }

  return <Home />;
};

// Enhanced Protected Route with authentication check
const ProtectedRoute = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

// Admin-only route protection
const AdminRoute = () => {
  const { user, isLoading, isAdmin } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/library" replace />;
  }

  return <Outlet />;
};

// Create router with nested routes and authentication protection
const router = createBrowserRouter([
  {
    path: "/",
    element: <HomeRoute />,
  },
  {
    path: "/v2",
    element: <LandingV2 />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/waitlist",
    element: <Waitlist />,
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/library",
        element: <Library />,
      },
      {
        path: "/chat",
        element: <Chat />,
      },
      {
        path: "/book/:id",
        element: <BookDetails />,
      },
      {
        path: "/settings",
        element: <Settings />,
      },
    ],
  },
  {
    element: <AdminRoute />,
    children: [
      {
        path: "/admin",
        element: <AdminDashboard />,
      },
      {
        path: "/admin/analytics",
        element: <AdminDashboard />,
      },
      {
        path: "/admin/invite-codes",
        element: <AdminInviteCodes />,
      },
      {
        path: "/admin/documents",
        element: <AdminDocuments />,
      },
      {
        path: "/admin/roles",
        element: <AdminDashboard />,
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

function App() {
  return (
    <AuthProvider>
      <BookProvider>
        <SessionProvider>
          <NotificationProvider>
            <VoiceChatProvider>
              <SessionManager />
              <Toaster />
              <Suspense fallback={<PageLoader />}>
                <RouterProvider router={router} />
              </Suspense>
            </VoiceChatProvider>
          </NotificationProvider>
        </SessionProvider>
      </BookProvider>
    </AuthProvider>
  );
}

export default App;
